import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  SegmentedControl,
  Select,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import {
  CheckCircle2,
  DoorOpen,
  LogIn,
  LogOut,
  MapPin,
  ScanLine,
  ShieldAlert,
  Users,
  XCircle,
} from 'lucide-react'
import { useRef, useState } from 'react'

import { ApiError, errorMessage } from '@/api/errors'
import {
  useAccessAttempt,
  useGymMembers,
  useLocations,
  useOccupancy,
  usePresence,
} from '@/api/queries'
import type {
  AccessAttemptResult,
  AccessDeniedReason,
  Location,
  PresenceEntry,
  Uuid,
} from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { LevelBadge, PresumedOutNote } from '@/features/occupancy/level'
import {
  deskEntry,
  deskLocation,
  occupancySettingsOf,
  passLocation,
  PRESUMED_EXIT_DEFAULT_MINUTES,
  presenceLocationId,
  presumedExitRule,
  type DeskLocation,
} from '@/features/occupancy/occupancy'
import { formatDateTime, plural } from '@/lib/format'
import { notify } from '@/lib/toast'
import { useSelectedOrganizationId } from '@/org/use-organization'

/**
 * La postazione della reception — ACC-03, ACC-04, ACC-07, QR-03.
 *
 * ## Un rifiuto non è un errore
 *
 * `POST /organization/access-events` risponde **`201` anche quando nega**: la
 * richiesta «registra questo tentativo» è riuscita, e l'evento esiste. Questa pagina
 * legge quindi `event.outcome` e non lo stato HTTP — un `catch` che mostra «errore di
 * rete» su un accesso negato è il modo più facile di sbagliare questa schermata, ed è
 * la ragione per cui il rifiuto compare come **esito** e non come avviso rosso di
 * sistema.
 *
 * ## Il riscontro visivo è il punto
 *
 * La risposta porta già nome e cognome dell'iscritto riconosciuto: la faccia sullo
 * schermo accanto alla persona al banco è ciò che impedisce a una tessera prestata di
 * passare. Non serve una seconda chiamata, e non va fatta — comporrebbe la schermata
 * in due tempi mentre qualcuno aspetta.
 *
 * ## Il lettore è un campo di testo
 *
 * Un lettore USB di codici a barre si presenta al sistema come una **tastiera**: il
 * modo di leggerlo è un campo con il fuoco, non una libreria. Il campo si svuota e si
 * rimette a fuoco dopo ogni lettura, perché la persona dopo è già lì — e anche dopo
 * ogni gesto sugli altri controlli della postazione: un lettore che scrive mentre il
 * fuoco è sull'interruttore Entrata/Uscita scrive nel vuoto.
 *
 * ## La sede del banco, in ogni passaggio
 *
 * Da ACC-07 ogni passaggio porta la sede (`locationId`): la lettura, l'ingresso
 * manuale, l'apertura forzata e l'uscita. Senza, in una palestra con più sedi
 * l'affollamento finisce nella voce «senza sede» e la sala vera resta a zero mentre è
 * piena; e il varco non può applicare la capienza (`location_full`), che è un attributo
 * della sede. Era un difetto latente — questa pagina la sede non la mandava, e quel ramo
 * del motore non si accendeva mai.
 *
 * Con una sede sola non c'è niente da scegliere e la pagina la dice soltanto. Con più
 * sedi il banco la sceglie una volta e la pagina la ricorda **su questo dispositivo**
 * (`localStorage`, per organizzazione): la sede è del banco e non di chi ci siede — al
 * cambio turno il collega trova la stessa —, ed è per questo che non si cancella alla
 * fine della sessione come l'organizzazione scelta. Finché non è scelta non si registra
 * niente: un passaggio senza sede è proprio il dato che ACC-07 chiede di non produrre.
 * Se le sedi non si leggono, invece, si registra senza: il banco non si ferma per una
 * lettura secondaria caduta.
 *
 * ## Le uscite
 *
 * Un'uscita **non si autorizza** (ACC-02): si registra e basta, con la stessa rotta
 * degli ingressi e `direction: 'out'`. È il gesto che rende il numero una misura invece
 * di una stima, quindi sta a un tocco in due posti: l'interruttore del lettore, per chi
 * esce passando dal banco, e la riga di ogni persona in «Chi è dentro adesso», per chi
 * le segna a fine turno. L'esito di un'uscita non dice «consentito» e non offre
 * l'apertura forzata: non c'è niente da forzare.
 *
 * L'interruttore resta dove lo si lascia, ed è una scelta: un banco davanti all'uscita lo
 * tiene su «Uscita» tutto il giorno. Il rovescio è il lettore dimenticato su «Uscita»
 * mentre la gente entra — senza nessuno dei controlli del motore, perché un'uscita non
 * si autorizza —, e per questo la modalità si vede: un avviso nel riquadro del lettore, e
 * un esito grigio «Uscita registrata» dove ci si aspetta il verde dell'ingresso. Al
 * ricaricamento si riparte da «Entrata».
 *
 * ## Chi è dentro, e chi non si conta più
 *
 * L'elenco e il numero in alto sono la stessa regola — ACC-04 con l'uscita presunta di
 * ACC-07 —, e il numero viene dall'affollamento e non dalla lunghezza dell'elenco: porta
 * con sé il livello della sala, e resta giusto anche quando l'elenco è troncato. Chi è
 * entrato da più dei minuti della sede senza un'uscita registrata non compare più, e
 * sotto l'elenco una riga dice quanti sono: al banco qualcuno chiederà «ma Rossi è
 * ancora dentro?», e la risposta deve stare sullo schermo.
 */
export const Route = createFileRoute('/_app/reception/')({
  component: ReceptionPage,
})

const DENIED_LABELS: Record<AccessDeniedReason, string> = {
  unknown_credential: 'Codice non riconosciuto',
  member_suspended: 'Iscritto sospeso',
  no_subscription: 'Nessun abbonamento attivo',
  subscription_expired: 'Abbonamento scaduto',
  no_entries_left: 'Ingressi esauriti',
  outside_access_window: 'Fuori dalla fascia oraria',
  location_full: 'Sala al completo',
  sport_card_expired: 'Tesseramento scaduto',
  anti_passback: 'Stessa tessera a un altro varco poco fa',
  // SRV-02. Il primo è il motivo per cui l'area esiste, e la frase deve dire a
  // chi sta al banco cosa fare: vendere, non far aspettare.
  service_not_included: 'L’abbonamento non include questo servizio',
  outside_service_window: 'Fuori dall’orario di questo servizio',
  service_monthly_limit_reached: 'Esauriti gli ingressi del mese per questo servizio',
  service_unavailable: 'Servizio non disponibile',
  // SRV-04. «Ingressi esauriti» sarebbe la frase dell'abbonamento, e manderebbe
  // a comprare la cosa sbagliata: chi ha finito le saune entra ancora in sala pesi.
  no_service_credits: 'Carnet del servizio esaurito',
}

const STORAGE_PREFIX = 'gymmi-admin.reception.locationId'

function readStoredLocation(key: string): string | null {
  try {
    const raw = localStorage.getItem(key)
    return raw && raw.trim() !== '' ? raw : null
  } catch {
    // Storage negato: la sede vale finché la pagina resta aperta. Non è un errore.
    return null
  }
}

function writeStoredLocation(key: string, locationId: string): void {
  try {
    localStorage.setItem(key, locationId)
  } catch {
    // Vedi sopra.
  }
}

/**
 * Il guscio: rimonta la postazione quando cambia l'organizzazione.
 *
 * Cambiare palestra svuota la cache ma non smonta la pagina, e qui c'è stato che appartiene a
 * **quella** palestra — la sede scelta, l'ultimo esito, il lettore su «Uscita». La chiave lo butta
 * via tutto insieme, ed è anche ciò che fa leggere la sede ricordata per la palestra nuova.
 */
function ReceptionPage() {
  const organizationId = useSelectedOrganizationId() ?? 'predefinita'
  return <ReceptionDesk key={organizationId} storageKey={`${STORAGE_PREFIX}.${organizationId}`} />
}

function ReceptionDesk({ storageKey }: { storageKey: string }) {
  const [code, setCode] = useState('')
  const [gate, setGate] = useState('reception')
  const [direction, setDirection] = useState<'in' | 'out'>('in')
  const [last, setLast] = useState<AccessAttemptResult | null>(null)
  const [overrideReason, setOverrideReason] = useState('')
  const [storedLocationId, setStoredLocationId] = useState(() => readStoredLocation(storageKey))
  const codeInput = useRef<HTMLInputElement>(null)

  const locations = useLocations()
  const desk = deskLocation({ data: locations.data, failed: locations.isError }, storedLocationId)
  const pass = passLocation(desk)
  const where: { locationId?: Uuid } =
    pass.locationId === undefined ? {} : { locationId: pass.locationId }
  const gateValue = gate.trim() === '' ? undefined : gate.trim()

  const attempt = useAccessAttempt()
  const occupancy = useOccupancy()
  const presence = usePresence(presenceLocationId(desk))
  const entry = deskEntry(occupancy.data, desk)

  async function submitCode() {
    const value = code.trim()
    // Senza la sede non si registra, e il codice resta nel campo: chi sceglie la sede lo
    // ritrova lì e lo manda con un Invio, invece di farlo rileggere alla persona al banco.
    if (value === '' || !pass.ready) return
    try {
      const result = await attempt.mutateAsync({
        method: 'qr',
        direction,
        code: value,
        ...where,
        gate: gateValue,
      })
      setLast(result)
    } catch {
      // Un vero errore — rete, permessi — lo mostra il blocco più sotto. Il rifiuto
      // non passa da qui: è un `201`.
    } finally {
      setCode('')
      setOverrideReason('')
      codeInput.current?.focus()
    }
  }

  /**
   * ACC-03: apre **nonostante** il rifiuto, e pretende un motivo. Il registro
   * conserva insieme il motivo per cui il sistema diceva no e la ragione per cui una
   * persona ha detto sì, ed è per questo che questo è un secondo gesto e non una
   * scorciatoia dentro il primo.
   */
  async function forceEntry() {
    const member = last?.gymMember
    if (member == null || overrideReason.trim() === '' || !pass.ready) return
    try {
      const result = await attempt.mutateAsync({
        method: 'manual',
        direction: 'in',
        gymMemberId: member.id,
        ...where,
        gate: gateValue,
        override: true,
        overrideReason: overrideReason.trim(),
      })
      setLast(result)
    } catch {
      // idem
    } finally {
      setOverrideReason('')
      codeInput.current?.focus()
    }
  }

  function chooseLocation(locationId: string) {
    setStoredLocationId(locationId)
    writeStoredLocation(storageKey, locationId)
    codeInput.current?.focus()
  }

  // Il numero viene dall'affollamento, che porta anche il livello; la presenza resta il
  // ripiego mentre quello arriva, così l'intestazione non resta vuota per mezzo secondo. Se non
  // arriva nessuno dei due la pastiglia non c'è: uno zero inventato direbbe «sala vuota».
  const inside =
    entry?.inside ??
    occupancy.data?.inside ??
    presence.data?.meta.total ??
    presence.data?.data.length
  // La soglia da dire sopra l'elenco: quella che l'affollamento ha applicato, o — finché non
  // arriva — quella scritta sulla sede. `null` è «mai», e non va confuso con «non so ancora».
  const deskPlace = desk.kind === 'single' || desk.kind === 'choose' ? desk.location : null
  const minutes =
    entry !== undefined
      ? entry.presumedExitAfterMinutes
      : deskPlace !== null
        ? occupancySettingsOf(deskPlace).presumedExitAfterMinutes
        : PRESUMED_EXIT_DEFAULT_MINUTES
  const rows = presence.data?.data ?? []

  return (
    <>
      <PageHeader
        title="Reception"
        description="Legge il QR dell'iscritto, registra ingressi e uscite e dice chi c'è in sala."
        actions={
          <>
            {inside === undefined ? null : (
              <Badge variant="neutral" iconStart={<Users />}>
                {plural(inside, 'persona in sala', 'persone in sala')}
              </Badge>
            )}
            {entry === undefined || entry.level === null ? null : (
              <LevelBadge level={entry.level} />
            )}
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
        <div className="border-border bg-card rounded-lg border p-4">
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <DeskLocationPicker desk={desk} onChoose={chooseLocation} />
            <Field label="Il lettore registra">
              {(props) => (
                <SegmentedControl
                  {...props}
                  fullWidth
                  value={direction}
                  onValueChange={(value) => {
                    setDirection(value === 'out' ? 'out' : 'in')
                    // Il fuoco torna al campo: il lettore scrive dove sta il fuoco, e sul
                    // controllo appena toccato la lettura successiva andrebbe persa.
                    codeInput.current?.focus()
                  }}
                  options={[
                    { value: 'in', label: 'Entrata', icon: <LogIn /> },
                    { value: 'out', label: 'Uscita', icon: <LogOut /> },
                  ]}
                />
              )}
            </Field>
          </div>

          {direction === 'out' ? (
            <Alert variant="warning" className="mb-3" title="Il lettore registra uscite">
              Nessun controllo sull’abbonamento: un’uscita non si autorizza, si registra e basta.
              Torna su «Entrata» per far entrare.
            </Alert>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-[3fr_1fr]">
            <Field
              label="Codice letto"
              hint="Il lettore USB scrive qui: basta che il campo abbia il fuoco."
            >
              {(props) => (
                <Input
                  {...props}
                  ref={codeInput}
                  autoFocus
                  value={code}
                  className="font-mono"
                  placeholder="7f3a9c21e8b04d56.492107"
                  onChange={(event) => setCode(event.target.value)}
                  onKeyDown={(event) => {
                    // I lettori chiudono la lettura con un Invio: è il segnale che
                    // il codice è completo, e aspettare un clic vorrebbe dire un
                    // gesto in più a ogni persona che entra.
                    if (event.key === 'Enter') void submitCode()
                  }}
                />
              )}
            </Field>
            <Field label="Varco">
              {(props) => (
                <Input {...props} value={gate} onChange={(event) => setGate(event.target.value)} />
              )}
            </Field>
          </div>

          <div className="mt-3 flex gap-2">
            <Button
              loading={attempt.isPending}
              disabled={!pass.ready}
              onClick={() => void submitCode()}
            >
              {direction === 'out' ? <LogOut /> : <ScanLine />}
              {direction === 'out' ? 'Registra uscita' : 'Registra ingresso'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setLast(null)
                setCode('')
                codeInput.current?.focus()
              }}
            >
              Pulisci
            </Button>
          </div>

          {attempt.isError ? (
            <Alert variant="danger" className="mt-3" title="La richiesta non è arrivata">
              {errorMessage(attempt.error)}
            </Alert>
          ) : null}

          {last !== null ? (
            <AttemptOutcome
              result={last}
              overrideReason={overrideReason}
              onOverrideReasonChange={setOverrideReason}
              onForce={() => void forceEntry()}
              forcing={attempt.isPending}
            />
          ) : (
            <p className="text-muted-foreground mt-4 text-sm">
              In attesa di una lettura. Chi non ha il telefono si cerca per nome qui sotto.
            </p>
          )}

          <ManualCheckIn
            gate={gateValue}
            where={where}
            onResult={setLast}
            disabled={attempt.isPending || !pass.ready}
          />
        </div>

        <div className="border-border bg-card rounded-lg border p-4">
          <h2 className="mb-1 font-semibold">Chi è dentro adesso</h2>
          <p className="text-muted-foreground mb-3 text-xs">
            L’ultimo passaggio consentito della giornata, per persona. Senza un’uscita registrata,{' '}
            {presumedExitRule(minutes)}.
          </p>

          {presence.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : presence.isError ? (
            // Un elenco vuoto per un errore direbbe «sala vuota», che è la bugia peggiore per
            // chi deve decidere se far entrare qualcuno.
            <ErrorState
              message={errorMessage(presence.error)}
              requestId={presence.error instanceof ApiError ? presence.error.requestId : undefined}
              onRetry={() => void presence.refetch()}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<DoorOpen />}
              title="Sala vuota"
              description="Nessuno risulta dentro adesso."
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {rows.map((row) => (
                <li key={row.gymMemberId}>
                  <PresenceRow row={row} gate={gateValue} where={where} disabled={!pass.ready} />
                </li>
              ))}
            </ul>
          )}

          {(presence.data?.meta.total ?? 0) > rows.length ? (
            // La presenza non si pagina (`nextCursor` è sempre `null`): un elenco troncato si
            // riconosce dal totale più grande delle righe arrivate.
            <p className="text-muted-foreground mt-2 text-xs">
              Elenco parziale: il conteggio in alto è quello vero.
            </p>
          ) : null}

          {entry === undefined ? null : (
            <PresumedOutNote
              className="mt-3"
              count={entry.presumedOut}
              minutes={entry.presumedExitAfterMinutes}
            />
          )}
        </div>
      </div>
    </>
  )
}

/**
 * La sede della postazione: scritta quando è una sola, scelta quando sono di più.
 *
 * Con più sedi e nessuna scelta l'avviso dice **perché** non si registra, che è la domanda di
 * chi trova il bottone spento al primo turno.
 */
function DeskLocationPicker({
  desk,
  onChoose,
}: {
  desk: DeskLocation<Location>
  onChoose: (locationId: string) => void
}) {
  switch (desk.kind) {
    case 'loading':
      return <Skeleton className="h-16 w-full" />
    case 'none':
      // Nessuna sede dichiarata: i passaggi valgono per la palestra intera, e non c'è
      // niente da scegliere né da dire.
      return null
    case 'unreadable':
      return (
        <Alert variant="warning" title="Sedi non disponibili">
          Non riesco a leggere le sedi: i passaggi vengono registrati senza sede finché la lettura
          non torna.
        </Alert>
      )
    case 'single':
      return (
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm font-medium">Sede</p>
          <p className="flex items-center gap-1.5 text-sm">
            <MapPin className="text-muted-foreground size-4 shrink-0" aria-hidden />
            {desk.location.name}
          </p>
        </div>
      )
    case 'choose':
      return (
        <div className="space-y-2">
          <Field
            label="Sede di questo banco"
            hint={desk.location === null ? undefined : 'Ricordata su questo dispositivo.'}
          >
            {(props) => (
              <Select
                {...props}
                // Stringa vuota e non `undefined`: il controllo resta controllato anche prima
                // della scelta, e mostra il segnaposto.
                value={desk.location?.id ?? ''}
                placeholder="Scegli la sede"
                onValueChange={onChoose}
                items={desk.options.map((location) => ({
                  value: location.id,
                  label: location.name,
                }))}
              />
            )}
          </Field>
          {desk.location === null ? (
            <Alert variant="warning" title="Scegli la sede prima di registrare">
              Con più sedi un passaggio senza sede non conta per nessuna sala, e la capienza non si
              applica.
            </Alert>
          ) : null}
        </div>
      )
  }
}

/**
 * Una persona in sala, con l'uscita a un tocco.
 *
 * L'uscita da qui è un passaggio manuale (`method: 'manual'`, `direction: 'out'`): la persona
 * non mostra niente, è chi sta al banco a dire che è uscita. La conferma è un avviso e non
 * l'esito grande del lettore, che resta di chi è davanti al banco adesso.
 */
function PresenceRow({
  row,
  gate,
  where,
  disabled,
}: {
  row: PresenceEntry
  gate: string | undefined
  where: { locationId?: Uuid }
  disabled: boolean
}) {
  const exit = useAccessAttempt()
  const name = `${row.lastName} ${row.firstName}`

  async function markExit() {
    try {
      const result = await exit.mutateAsync({
        method: 'manual',
        direction: 'out',
        gymMemberId: row.gymMemberId,
        ...where,
        gate,
      })
      const reason = result.event.deniedReason
      notify(
        result.event.outcome === 'allowed'
          ? { message: `Uscita registrata: ${name}.`, variant: 'success' }
          : {
              message: `Uscita non registrata: ${reason === null ? 'rifiutata' : DENIED_LABELS[reason].toLowerCase()}.`,
              variant: 'danger',
            },
      )
    } catch (error) {
      notify({ message: errorMessage(error), variant: 'danger' })
    }
  }

  return (
    // `flex-row` esplicito: la `Card` del design system è una colonna, e senza il nome e il
    // bottone finirebbero uno sotto l'altro.
    <Card
      radius="md"
      padding="sm"
      className="flex flex-row items-center justify-between gap-2 py-2"
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{name}</span>
        <span className="text-muted-foreground block text-xs" data-numeric>
          {formatDateTime(row.since)}
        </span>
      </span>
      <Button
        size="sm"
        variant="ghost"
        // Il nome visibile dentro quello accessibile: dieci «Segna uscita» uguali, letti in
        // fila, non direbbero di chi.
        aria-label={`Segna uscita: ${name}`}
        disabled={disabled}
        loading={exit.isPending}
        onClick={() => void markExit()}
      >
        <LogOut />
        Segna uscita
      </Button>
    </Card>
  )
}

function AttemptOutcome({
  result,
  overrideReason,
  onOverrideReasonChange,
  onForce,
  forcing,
}: {
  result: AccessAttemptResult
  overrideReason: string
  onOverrideReasonChange: (value: string) => void
  onForce: () => void
  forcing: boolean
}) {
  const allowed = result.event.outcome === 'allowed'
  const exit = result.event.direction === 'out'
  const member = result.gymMember
  const subscription = result.subscription
  const reason = result.event.deniedReason

  return (
    <div
      className={
        !allowed
          ? 'border-danger/40 bg-danger/10 mt-4 rounded-md border p-3'
          : exit
            ? // Grigio e non verde: un'uscita non è un permesso accordato, e il colore diverso
              // è ciò che fa accorgere di un lettore lasciato su «Uscita».
              'border-border bg-muted/60 mt-4 rounded-md border p-3'
            : 'border-success/40 bg-success/10 mt-4 rounded-md border p-3'
      }
    >
      <div className="flex items-center gap-2">
        {!allowed ? (
          <XCircle className="text-danger-content size-5" />
        ) : exit ? (
          <LogOut className="text-muted-foreground size-5" />
        ) : (
          <CheckCircle2 className="text-success-content size-5" />
        )}
        <p className="font-semibold">
          {allowed
            ? exit
              ? 'Uscita registrata'
              : 'Ingresso consentito'
            : reason != null
              ? DENIED_LABELS[reason]
              : exit
                ? 'Uscita non registrata'
                : 'Ingresso negato'}
        </p>
        {result.event.overrideReason != null ? <Badge variant="warning">Forzato</Badge> : null}
      </div>

      <p className="mt-1 text-sm">
        {member != null ? `${member.lastName} ${member.firstName}` : 'Nessuno riconosciuto'}
        {member?.hasPhoto === false ? ' · senza foto' : ''}
      </p>

      {subscription != null ? (
        <p className="text-muted-foreground mt-1 text-xs">
          {subscription.productName}
          {subscription.entriesRemaining != null
            ? ` · ${plural(subscription.entriesRemaining, 'ingresso residuo', 'ingressi residui')}`
            : ''}
        </p>
      ) : null}

      {/* L'override esiste solo su un **ingresso** rifiutato **con una persona
          riconosciuta**: su un codice sconosciuto non c'è nessuno a cui aprire, e
          un'uscita non si autorizza — non c'è un no da scavalcare. */}
      {!allowed && !exit && member != null ? (
        <div className="mt-3 grid gap-2 sm:grid-cols-[2fr_auto]">
          <Field label="Motivo dell'apertura forzata">
            {(props) => (
              <Input
                {...props}
                value={overrideReason}
                placeholder="Rinnova domani, autorizzato dal titolare"
                onChange={(event) => onOverrideReasonChange(event.target.value)}
              />
            )}
          </Field>
          <div className="flex items-end">
            <Button
              variant="outline"
              disabled={overrideReason.trim() === ''}
              loading={forcing}
              onClick={onForce}
            >
              <ShieldAlert />
              Apri comunque
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * ACC-03, il check-in manuale: chi ha dimenticato il telefono si cerca per nome.
 *
 * La ricerca è la stessa dell'elenco iscritti — nome, cognome, numero di tessera — e
 * **non** il codice fiscale, per la ragione dichiarata nel contract: una ricerca
 * esatta su quel campo sarebbe un oracolo che conferma un dato ipotizzato.
 */
function ManualCheckIn({
  gate,
  where,
  onResult,
  disabled,
}: {
  gate: string | undefined
  where: { locationId?: Uuid }
  onResult: (result: AccessAttemptResult) => void
  disabled: boolean
}) {
  const [query, setQuery] = useState('')
  const attempt = useAccessAttempt()
  const list = useGymMembers({ query: query.trim() === '' ? undefined : query.trim(), limit: 5 })

  async function checkIn(gymMemberId: string) {
    try {
      onResult(
        await attempt.mutateAsync({
          method: 'manual',
          direction: 'in',
          gymMemberId,
          ...where,
          gate,
        }),
      )
      setQuery('')
    } catch {
      // Come sopra: il rifiuto arriva come esito, non come eccezione.
    }
  }

  return (
    <div className="border-border mt-4 border-t pt-4">
      <Field label="Chi non ha il telefono" hint="Nome, cognome o numero di tessera.">
        {(props) => (
          <Input
            {...props}
            value={query}
            placeholder="Rossi"
            onChange={(event) => setQuery(event.target.value)}
          />
        )}
      </Field>

      {query.trim() !== '' ? (
        list.isPending ? (
          <Skeleton className="mt-2 h-16 w-full" />
        ) : (
          <ul className="mt-2 flex flex-col gap-2">
            {(list.data?.data ?? []).map((member) => (
              <li key={member.id}>
                <Card
                  radius="md"
                  padding="sm"
                  className="flex flex-row items-center justify-between gap-2 py-2"
                >
                  <span className="truncate text-sm">
                    {member.lastName} {member.firstName}
                    {member.memberNumber != null ? (
                      <span className="text-muted-foreground"> · {member.memberNumber}</span>
                    ) : null}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={disabled || attempt.isPending}
                    onClick={() => void checkIn(member.id)}
                  >
                    Fai entrare
                  </Button>
                </Card>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  )
}
